/**
 * Envoie un blob LOCAL (téléchargement / import du téléphone) au serveur et
 * renvoie une URL ABSOLUE que la baffle (TV) peut STREAMER via `audioUrl`.
 *
 * Sert à jouer sur la baffle un morceau qui n'existe QUE en local sur le
 * téléphone (import de fichier, ou téléchargement YouTube quand le re-stream
 * serveur est bloqué). Renvoie null si l'upload échoue (l'appelant retombe
 * alors sur le `videoId` seul → re-stream YouTube).
 *
 * XHR (pas fetch) pour la PROGRESSION d'envoi : un import peut faire >100 Mo
 * → sans pourcentage l'utilisateur croit que rien ne se passe et re-clique.
 *
 * ⚠️ REPRISE AUTOMATIQUE : sur mobile, la connexion peut couper EN COURS de
 * transfert (Wi-Fi instable, passage 4G/Wi-Fi) → l'upload échouait en silence
 * (`up:FAIL`) et le morceau local n'arrivait jamais sur la TV. On réessaie donc
 * plusieurs fois sur une erreur RÉSEAU (mais PAS sur un refus déterministe du
 * serveur, ex. fichier trop gros).
 */

function uploadBeacon(msg: string) {
  try { const x = new XMLHttpRequest(); x.open('POST', '/api/tvlog', true); x.send('[baffleup-cli] ' + msg) } catch {}
}

type Attempt = { url: string } | { retry: true; reason: string } | { fatal: true; reason: string }

function attemptUpload(
  blob: Blob,
  fileName: string,
  onProgress?: (frac: number) => void,
): Promise<Attempt> {
  return new Promise((resolve) => {
    try {
      const fd = new FormData()
      fd.append('file', blob, fileName)
      const x = new XMLHttpRequest()
      x.open('POST', '/api/party/upload', true)
      x.timeout = 10 * 60 * 1000   // 10 min : gros fichier sur Wi-Fi lent
      if (x.upload && onProgress) {
        x.upload.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total) }
      }
      x.onload = () => {
        // 5xx = souci serveur transitoire → on réessaie. 4xx = refus définitif.
        if (x.status >= 500) { resolve({ retry: true, reason: 'http' + x.status }); return }
        if (x.status >= 400) { resolve({ fatal: true, reason: 'http' + x.status }); return }
        try {
          const j = JSON.parse(x.responseText)
          if (j?.ok && j.url) resolve({ url: location.origin + j.url })
          else resolve({ fatal: true, reason: 'noturl' })
        } catch { resolve({ retry: true, reason: 'parse' }) }
      }
      x.onerror = () => resolve({ retry: true, reason: 'neterr' })   // coupure réseau → réessayable
      x.ontimeout = () => resolve({ retry: true, reason: 'timeout' })
      x.send(fd)
    } catch (e: any) { resolve({ retry: true, reason: 'throw:' + (e?.message || '?') }) }
  })
}

export async function uploadLocalForBaffle(
  blob: Blob,
  fileName?: string,
  videoId?: string,
  onProgress?: (frac: number) => void,
): Promise<string | null> {
  const name = fileName || (videoId ? `${videoId}.mp3` : 'track.mp3')
  const MAX_TRIES = 3
  for (let i = 0; i < MAX_TRIES; i++) {
    const r = await attemptUpload(blob, name, onProgress)
    if ('url' in r) {
      if (i > 0) uploadBeacon('OK après ' + (i + 1) + ' essais vid=' + (videoId || '?'))
      return r.url
    }
    if ('fatal' in r) {
      uploadBeacon('ABANDON (refus serveur ' + r.reason + ') vid=' + (videoId || '?'))
      return null
    }
    // erreur réseau réessayable
    uploadBeacon('essai ' + (i + 1) + '/' + MAX_TRIES + ' KO (' + r.reason + ') vid=' + (videoId || '?') + ' taille=' + blob.size)
    if (i < MAX_TRIES - 1) await new Promise(res => setTimeout(res, 1000 * (i + 1)))
  }
  return null
}
